// MA-C21 — shared shape/color glyph helpers for the `shape_array`
// (ms.math.00f, "Which shape is on the card?") and `pattern_strip`
// (ms.math.00g, "What comes next?") renderers. Both draw the same small set
// of named shapes in a named color, so the drawing primitive + the
// "<color> <shape>" token parser (pattern_strip's authored `shown` entries,
// e.g. "blue square") live here once instead of duplicated per renderer.
import type { ReactElement } from "react";

const SHAPE_COLOR_MAP: Record<string, string> = {
  purple: "var(--pal-purple, #a98ac9)",
  orange: "var(--pal-orange, #e89b6b)",
  teal: "var(--pal-teal, #4f9d95)",
  red: "var(--pal-red, #c0574a)",
  green: "var(--pal-green, #88b591)",
  blue: "var(--pal-blue, #6e9ccf)",
  yellow: "var(--pal-yellow, #e8c461)",
};
const FALLBACK_FILL = "var(--color-border-secondary, #cdbfae)";

/** Fail-open: an unrecognized/absent authored color never throws — it falls
 *  back to the neutral fill (mirrors TwoColorTenFrameRenderer's precedent). */
export function colorToken(color?: string): string {
  return (typeof color === "string" && SHAPE_COLOR_MAP[color]) || FALLBACK_FILL;
}

const KNOWN_SHAPES = new Set(["circle", "square", "triangle", "rectangle"]);

/** MA-691 — true for exactly the colour words `colorToken` draws (never the
 *  neutral fallback). The option-tile glyph mode fails closed on any other
 *  colour word so a tile can never draw a piece in a hue the word does not
 *  name. */
export function isKnownShapeColor(color: unknown): color is string {
  return typeof color === "string" && Object.prototype.hasOwnProperty.call(SHAPE_COLOR_MAP, color);
}

export function isKnownShape(shape: unknown): shape is string {
  return typeof shape === "string" && KNOWN_SHAPES.has(shape);
}

type ShapeStyleFamily = "outline" | "filled" | "small" | "large";

/** MA-C21 — the 6 authored `displayStyle` values ("outline" | "small" |
 *  "large" | "filled" | "solid" | "thick outline") collapse into 4 cosmetic
 *  drawing FAMILIES. Never dropped as decorative noise — the real corpus
 *  round-robins all 6 as a deliberate "shape constancy" pedagogy variation
 *  (a child should recognize a circle whether it's big, small, outlined, or
 *  filled) — just grouped for rendering. Unknown/absent → "filled". */
export function styleFamily(displayStyle?: string): ShapeStyleFamily {
  if (displayStyle === "outline" || displayStyle === "thick outline") return "outline";
  if (displayStyle === "small") return "small";
  if (displayStyle === "large") return "large";
  return "filled"; // "filled" | "solid" | unknown
}

/** Draw one shape glyph centered at (cx, cy). `outline` families stroke-only
 *  (fill="none"); every other family fills. Returns null for an unknown
 *  shape (fail-safe: callers must not draw a misleading blank/wrong glyph). */
export function shapeGlyph(
  shape: string,
  cx: number,
  cy: number,
  size: number,
  fill: string,
  family: ShapeStyleFamily,
  keyProp?: string | number,
  outlineWidth = 5,
  // MA-691 (RED M1/S1) — an optional non-hue EDGE on a filled glyph (the
  // option tiles pass a 2.5px --ink edge so every piece keeps >= 3:1 against
  // every tile state, including the solid-green reveal). Omitted (the strip,
  // the shape card, the PAT hint) -> byte-identical to before.
  edge?: { stroke: string; strokeWidth: number },
): ReactElement | null {
  const outline = family === "outline";
  const common = outline
    ? { fill: "none", stroke: fill, strokeWidth: outlineWidth }
    : edge
      ? { fill, stroke: edge.stroke, strokeWidth: edge.strokeWidth, strokeLinejoin: "round" as const }
      : { fill, stroke: "none" };
  switch (shape) {
    case "circle":
      return <circle key={keyProp} cx={cx} cy={cy} r={size} {...common} data-shape="circle" />;
    case "square":
      return (
        <rect key={keyProp} x={cx - size} y={cy - size} width={size * 2} height={size * 2} rx="4"
          {...common} data-shape="square" />
      );
    case "rectangle":
      return (
        <rect key={keyProp} x={cx - size * 1.3} y={cy - size * 0.75} width={size * 2.6} height={size * 1.5} rx="4"
          {...common} data-shape="rectangle" />
      );
    case "triangle":
      return (
        <path key={keyProp}
          d={`M${cx},${cy - size} L${cx - size},${cy + size} L${cx + size},${cy + size} Z`}
          {...common} data-shape="triangle" />
      );
    default:
      return null;
  }
}

/** Parse a "<color> <shape>" token (e.g. "blue square") into its parts.
 *  Fails soft — an unrecognized shape/color still returns whatever COULD be
 *  parsed; callers treat a missing `shape` as "draw nothing", never throw. */
export function parseColoredShapeToken(token: string): { color?: string; shape?: string } {
  const words = String(token ?? "").trim().toLowerCase().split(/\s+/);
  const shape = words.find((w) => KNOWN_SHAPES.has(w));
  const color = words.find((w) => w in SHAPE_COLOR_MAP);
  return { color, shape };
}
